// "Has this agent signed their agreement?" — one answer, three sources.
//
// Plain .js with no JSX so the backend suite can import it (same reasoning as
// agreements-gate.js). This rule has now been got wrong twice in two different
// components, both times in a way that reported a genuinely executed contract
// as unsigned:
//
//   1. StepFinish read `agreements.package` unconditionally. Recruits sign the
//      standalone ICA, which lands in `agreements.everyplace`, so packageSigned
//      was permanently false for every recruit.
//   2. StepFinish then read `agreements` alone. That is session state written
//      only by the signing modal — and signing happens in a SEPARATE BoldSign
//      tab, so the session that reaches Finish usually never saw it land.
//
// The second one was not cosmetic: these strings feed the recap email, the
// Notion status and onboarding_milestones.notes, so the wrong answer was
// written into three records.
//
// The sources, in order of freshness:
//   agreements — session state; most current between signing and the webhook
//   gate       — polled from the server; the recruit ICA signal
//   serverDocs — fetched from /v1/documents/mine; refetched when the gate flips
//
// Signed by ANY of them means signed. None of them can produce a false positive:
// each is derived from a completed envelope.

export function agreementSignedState({ recruit, agreements: agreementsArg, gate, serverDocs } = {}) {
  // `= {}` in the destructure only covers undefined, not an explicit null — and
  // App.jsx's state can be either before the first fetch resolves.
  const agreements = agreementsArg || {};
  // Recruits sign one document (the ICA); transfer-day agents sign a bundle.
  const agreementKey = recruit ? "everyplace" : "package";
  const docType      = recruit ? "ica" : "everyplace_package";
  const label        = recruit ? "Independent Contractor Agreement" : "Agreement package";

  const sessionSigned = !!agreements[agreementKey]?.signed;
  // gate.icaDone only speaks for the recruit ICA — never let it stand in for a
  // transfer-day agent's bundle, which contains documents it knows nothing about.
  const gateSigned    = !!recruit && !!gate?.icaDone;
  const docSigned     = !!serverDocs?.[docType]?.signed;

  const signed = sessionSigned || gateSigned || docSigned;

  // Prefer the session's own date (it has one the moment the modal closes),
  // then the server's. Never return a date when nothing is signed.
  const when = !signed
    ? ""
    : agreements[agreementKey]?.when || fmtDate(serverDocs?.[docType]?.updatedAt) || "";

  return { signed, when, label, agreementKey, docType };
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
